import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Select, Checkbox } from 'antd';
import { ChevronLeftIcon } from '../../assets/icons';
import { BIND_CHIP, BIND_LABEL, isOwn } from '../admin/platform/mockData';
import {
  useGetClientContextQuery,
  useGetStateDepartmentQuery,
} from '../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Client Admin — choose levels for a department, routed by code:
 * /client/departments/:code. The department + its real levels come from the
 * client's state (states/<code>/departments/<tcode>/); the include/skip choice
 * is local (no client "selected levels" endpoint yet).
 * ------------------------------------------------------------------ */

export default function ClientDepartmentConfigPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { data: ctx } = useGetClientContextQuery();
  const clientState = ctx?.state_code ?? '';
  const { data: dept, isLoading } = useGetStateDepartmentQuery(
    { code: clientState, tcode: code ?? '' },
    { skip: !clientState || !code },
  );
  const [skip, setSkip] = useState<Set<number>>(new Set());

  if (isLoading) {
    return <p className="p-2 text-sm text-ink-secondary">Loading department…</p>;
  }
  if (!dept) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-secondary">Department not found.</p>
        <button
          type="button"
          onClick={() => navigate('/client/departments')}
          className="inline-flex h-10 w-fit items-center gap-1.5 rounded-lg border border-line bg-white pl-2.5 pr-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
        >
          <ChevronLeftIcon className="h-3 w-[7px]" />
          All hierarchies
        </button>
      </div>
    );
  }

  const levels = dept.levels;
  const includedCount = levels.length - skip.size;
  const ownIncluded = levels.filter((l, i) => isOwn(l.binding) && !skip.has(i)).length;
  const lastIncluded = levels.reduce((acc, _, i) => (!skip.has(i) ? i : acc), 0);

  const toggle = (i: number) =>
    setSkip((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const setDownTo = (idx: number) =>
    setSkip(() => {
      const next = new Set<number>();
      levels.forEach((_, i) => {
        if (i > idx) next.add(i);
      });
      return next;
    });

  const warnings: string[] = [];
  let lastAnchorSkipped = false;
  levels.forEach((l, i) => {
    if (l.binding === 'anchor') lastAnchorSkipped = skip.has(i);
    if (l.binding === 'inherited' && !skip.has(i) && lastAnchorSkipped) {
      warnings.push(`${l.name} is inherited, but its anchor above is skipped — include the anchor or give it its own binding.`);
    }
  });

  return (
    <div className="flex flex-col gap-[18px]">
      <div>
        <button
          type="button"
          onClick={() => navigate('/client/departments')}
          className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-line bg-white pl-2.5 pr-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
        >
          <ChevronLeftIcon className="h-3 w-[7px]" />
          All hierarchies
        </button>
      </div>

      <section className="flex max-w-[820px] flex-col gap-3.5 rounded-xl border border-line/30 bg-white p-6 shadow-card">
        <div>
          <div className="text-xs text-ink-secondary">STEP 2 OF 2 · CHOOSE LEVELS · TEMPLATE {dept.code}</div>
          <h1 className="mb-1.5 mt-1 text-2xl font-semibold text-ink">{dept.name}</h1>
          <p className="text-sm text-ink-secondary">
            Include only the levels this client needs. A good rule: stop at the level whose officer
            takes requests. Skipped levels can be added back at any time.
          </p>
        </div>

        <div className="flex max-w-[320px] flex-col gap-1">
          <label className="text-xs font-semibold text-ink-secondary">Include down to</label>
          <Select
            size="large"
            className="w-full"
            value={lastIncluded}
            onChange={(v) => setDownTo(Number(v))}
            options={levels.map((l, i) => ({ value: i, label: l.name }))}
          />
        </div>

        <div className="flex flex-col border-t border-[#f3f4f6]">
          {levels.map((l, i) => {
            const on = !skip.has(i);
            return (
              <div
                key={i}
                className={['grid grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-2.5 border-b border-[#f3f4f6] py-2.5', on ? '' : 'opacity-55'].join(' ')}
              >
                <Checkbox checked={on} onChange={() => toggle(i)} />
                <span className="font-semibold text-ink" style={{ paddingLeft: i * 12 }}>
                  {l.name}
                </span>
                <span className={['inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold', BIND_CHIP[l.binding]].join(' ')}>
                  {l.binding_label || BIND_LABEL[l.binding]}
                </span>
              </div>
            );
          })}
        </div>

        {warnings.map((w) => (
          <div key={w} className="rounded-md border border-[#e9a3a3] bg-[#fdecec] px-3 py-2 text-[13px] text-[#7f1d1d]">
            {w}
          </div>
        ))}

        <div className="text-[13px] font-semibold text-ink">
          {includedCount} of {levels.length} template levels included · {ownIncluded} own level
          {ownIncluded === 1 ? '' : 's'} to map
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={includedCount === 0}
            onClick={() => navigate('/client/departments')}
            className="h-11 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            Add department
          </button>
          <button
            type="button"
            onClick={() => navigate('/client/departments')}
            className="h-11 rounded-lg border border-line bg-white px-4 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
          >
            Choose another template
          </button>
        </div>
      </section>
    </div>
  );
}
